import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Crosshair, Sparkle, Link as LinkIcon, MagnifyingGlass, Check, X, Warning, Globe, FloppyDisk } from "@phosphor-icons/react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const TABS = [
  { k: "search", label: "Natural-Language Search", icon: MagnifyingGlass },
  { k: "urls", label: "Paste URLs", icon: LinkIcon },
];

const confidenceBadge = (c) => {
  const n = Number(c || 0);
  if (n >= 0.75) return { txt: "HIGH", cls: "bg-[#0D9488] text-white" };
  if (n >= 0.45) return { txt: "MED", cls: "bg-[#F4A261] text-[#0A0A0A]" };
  return { txt: "LOW", cls: "bg-[#E5E7EB] text-[#4A4D57]" };
};

export default function LeadHunt() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("search");
  const [status, setStatus] = useState(null);
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(6);
  const [urls, setUrls] = useState("");
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null); // { leads, pages, total, duplicates }
  const [selected, setSelected] = useState({}); // idx -> true
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/leads/hunt/status");
        setStatus(data);
      } catch (e) {
        setStatus({ configured: false, firecrawl_configured: false, openai_configured: false });
      }
    })();
  }, []);

  const canRun = status?.configured && !running;
  const nonDups = useMemo(() => (results?.leads || []).filter((l) => !l._duplicate), [results]);
  const selectedCount = useMemo(() => Object.values(selected).filter(Boolean).length, [selected]);

  const runSearch = async () => {
    if (!query.trim()) { toast.error("Type a search query"); return; }
    setRunning(true); setResults(null); setSelected({});
    try {
      const { data } = await api.post("/leads/hunt/search", { query: query.trim(), limit: Number(limit) || 6 }, { timeout: 240000 });
      setResults(data);
      // Pre-select all non-duplicates
      const sel = {};
      (data.leads || []).forEach((l, i) => { if (!l._duplicate) sel[i] = true; });
      setSelected(sel);
      toast.success(`Found ${data.total} candidate(s) · ${data.duplicates} duplicate(s) flagged`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    } finally { setRunning(false); }
  };

  const runScrape = async () => {
    const list = urls.split(/[\s,]+/).map((u) => u.trim()).filter((u) => u && /^https?:\/\//i.test(u));
    if (!list.length) { toast.error("Paste at least one URL (must start with http/https)"); return; }
    if (list.length > 10) { toast.error("Max 10 URLs per run (free-tier guardrail)"); return; }
    setRunning(true); setResults(null); setSelected({});
    try {
      const { data } = await api.post("/leads/hunt/scrape", { urls: list }, { timeout: 240000 });
      setResults(data);
      const sel = {};
      (data.leads || []).forEach((l, i) => { if (!l._duplicate) sel[i] = true; });
      setSelected(sel);
      toast.success(`Scraped ${data.pages?.length || 0} page(s) · ${data.total} lead(s) · ${data.duplicates} duplicate(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scrape failed");
    } finally { setRunning(false); }
  };

  const toggleAll = (on) => {
    const sel = {};
    (results?.leads || []).forEach((l, i) => { if (!l._duplicate) sel[i] = !!on; });
    setSelected(sel);
  };

  const saveSelected = async () => {
    const picked = (results?.leads || []).filter((_, i) => selected[i] && !results.leads[i]._duplicate);
    if (!picked.length) { toast.error("Select at least one non-duplicate lead"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/leads/hunt/save", { leads: picked });
      toast.success(`Saved ${data.saved_count} lead(s) to CRM` + (data.skipped_count ? ` · ${data.skipped_count} skipped` : ""));
      // Remove saved ones from preview
      const survivingIdx = new Set();
      (results.leads || []).forEach((l, i) => { if (!selected[i] || l._duplicate) survivingIdx.add(i); });
      const newLeads = (results.leads || []).filter((_, i) => survivingIdx.has(i));
      setResults({ ...results, leads: newLeads, total: newLeads.length });
      setSelected({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Crosshair size={28} weight="bold" className="text-[#D90429]"/>
            <h1 className="font-display text-3xl sm:text-4xl tracking-tight">AI Lead Hunt</h1>
            <span className="font-mono-shelv text-[10px] tracking-widest uppercase px-2 py-0.5 bg-[#0A0A0A] text-white">BETA</span>
          </div>
          <p className="text-sm text-[#4A4D57] max-w-2xl">
            Hunt prospects across the web using <b>Firecrawl</b> for scraping and <b>OpenAI gpt-4o-mini</b> for structured extraction.
            Review AI suggestions, then push the non-duplicates straight into your Leads pipeline.
          </p>
        </div>

        {/* Status / key warning */}
        {status && !status.configured && (
          <div className="border border-[#D90429]/30 bg-[#D90429]/5 p-4 text-sm" data-testid="hunt-config-warning">
            <div className="flex items-start gap-3">
              <Warning size={18} weight="bold" className="text-[#D90429] flex-shrink-0 mt-0.5"/>
              <div className="flex-1">
                <div className="font-semibold text-[#0A0A0A] mb-1">API keys not configured</div>
                <ul className="text-[13px] text-[#4A4D57] list-disc ml-4 space-y-1">
                  {!status.firecrawl_configured && <li>Set <code className="font-mono-shelv">FIRECRAWL_API_KEY</code> in <code>backend/.env</code> — get one at <a href="https://firecrawl.dev" target="_blank" rel="noreferrer" className="underline text-[#D90429]">firecrawl.dev</a> (free tier 500 pages / month).</li>}
                  {!status.openai_configured && <li>Set <code className="font-mono-shelv">OPENAI_API_KEY</code> in <code>backend/.env</code> — get one at <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer" className="underline text-[#D90429]">platform.openai.com</a>.</li>}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-0 border-b border-black/10">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                data-testid={`hunt-tab-${t.k}`}
                className={`px-5 py-3 text-sm font-semibold inline-flex items-center gap-2 border-b-2 transition-colors ${
                  tab === t.k ? "border-[#D90429] text-[#0A0A0A]" : "border-transparent text-[#717680] hover:text-[#0A0A0A]"
                }`}
              >
                <Icon size={16} weight="bold"/> {t.label}
              </button>
            );
          })}
        </div>

        {/* Input panel */}
        <div className="bg-white border border-black/10 p-5 sm:p-6 space-y-4">
          {tab === "search" ? (
            <>
              <div>
                <Label className="text-xs font-mono-shelv uppercase tracking-widest text-[#4A4D57]">Natural-Language Query</Label>
                <Textarea
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder='e.g. "100+ bed multispecialty hospitals in Mizoram and Manipur that need oxygen plants"'
                  rows={3}
                  className="rounded-none mt-1"
                  data-testid="hunt-search-input"
                />
                <p className="text-[11px] text-[#717680] mt-1">Be specific — city, industry, scale, requirement.</p>
              </div>
              <div className="flex items-end gap-3 flex-wrap">
                <div>
                  <Label className="text-xs font-mono-shelv uppercase tracking-widest text-[#4A4D57]">Pages to scan</Label>
                  <Input type="number" min={1} max={10} value={limit} onChange={(e) => setLimit(e.target.value)} className="w-24 rounded-none mt-1" data-testid="hunt-limit-input"/>
                </div>
                <button
                  onClick={runSearch}
                  disabled={!canRun}
                  data-testid="hunt-search-button"
                  className="px-5 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] disabled:opacity-40 inline-flex items-center gap-2"
                >
                  <Sparkle size={14} weight="bold"/> {running ? "Hunting…" : "Hunt with AI"}
                </button>
              </div>
            </>
          ) : (
            <>
              <div>
                <Label className="text-xs font-mono-shelv uppercase tracking-widest text-[#4A4D57]">URLs to scrape (one per line, max 10)</Label>
                <Textarea
                  value={urls}
                  onChange={(e) => setUrls(e.target.value)}
                  placeholder={"https://example-hospital.com/about\nhttps://industry-directory.com/page-3"}
                  rows={5}
                  className="rounded-none mt-1 font-mono-shelv text-[13px]"
                  data-testid="hunt-urls-input"
                />
              </div>
              <button
                onClick={runScrape}
                disabled={!canRun}
                data-testid="hunt-scrape-button"
                className="px-5 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] disabled:opacity-40 inline-flex items-center gap-2"
              >
                <Globe size={14} weight="bold"/> {running ? "Scraping…" : "Scrape & Extract"}
              </button>
            </>
          )}
          {running && (
            <div className="flex items-center gap-2 text-xs text-[#0D9488] font-semibold">
              <span className="inline-block w-3 h-3 border-2 border-[#0D9488] border-t-transparent rounded-full animate-spin"/>
              Firecrawl is fetching pages · OpenAI is parsing leads (this can take 30–90 seconds)
            </div>
          )}
        </div>

        {/* Results */}
        {results && (
          <div className="bg-white border border-black/10" data-testid="hunt-results">
            <div className="px-5 py-4 border-b border-black/10 flex items-center justify-between flex-wrap gap-3">
              <div className="text-sm">
                <span className="font-display text-lg">{results.total}</span>
                <span className="text-[#717680] ml-2">candidate(s)</span>
                {results.duplicates > 0 && (
                  <span className="ml-3 text-[12px] px-2 py-0.5 bg-[#F4A261]/20 text-[#0A0A0A] font-semibold">
                    {results.duplicates} duplicate(s) flagged
                  </span>
                )}
                {nonDups.length > 0 && (
                  <span className="ml-2 text-[12px] text-[#717680]">{selectedCount} of {nonDups.length} selected</span>
                )}
              </div>
              <div className="flex gap-2">
                <button onClick={() => toggleAll(true)} className="text-xs px-3 py-1.5 border border-black/15 hover:bg-[#F5F6F8]" data-testid="hunt-select-all">Select all</button>
                <button onClick={() => toggleAll(false)} className="text-xs px-3 py-1.5 border border-black/15 hover:bg-[#F5F6F8]" data-testid="hunt-clear-all">Clear</button>
                <button
                  onClick={saveSelected}
                  disabled={!selectedCount || saving}
                  data-testid="hunt-save-button"
                  className="text-xs px-4 py-1.5 bg-[#0D9488] text-white font-semibold hover:bg-[#0F766E] disabled:opacity-40 inline-flex items-center gap-1.5"
                >
                  <FloppyDisk size={12} weight="bold"/> {saving ? "Saving…" : `Save ${selectedCount} to CRM`}
                </button>
              </div>
            </div>

            {!results.leads?.length && (
              <div className="p-8 text-center text-sm text-[#717680]">
                No leads extracted — try a more specific query or different URLs.
              </div>
            )}

            <div className="divide-y divide-black/5">
              {(results.leads || []).map((l, i) => {
                const conf = confidenceBadge(l.confidence);
                const dup = !!l._duplicate;
                return (
                  <div key={i} className={`p-4 sm:p-5 ${dup ? "bg-[#F4A261]/5" : ""}`} data-testid={`hunt-lead-${i}`}>
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={!!selected[i]}
                        disabled={dup}
                        onChange={(e) => setSelected({ ...selected, [i]: e.target.checked })}
                        className="mt-1 accent-[#D90429]"
                        data-testid={`hunt-checkbox-${i}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-display text-lg text-[#0A0A0A] truncate">{l.company}</h3>
                              <span className={`text-[10px] px-1.5 py-0.5 font-mono-shelv font-bold tracking-wider ${conf.cls}`}>{conf.txt} {l.confidence ?? "—"}</span>
                              {dup && (
                                <span className="text-[10px] px-1.5 py-0.5 bg-[#D90429] text-white font-mono-shelv font-bold tracking-wider inline-flex items-center gap-1">
                                  <Warning size={10} weight="bold"/> DUPLICATE
                                </span>
                              )}
                            </div>
                            {l.industry && <div className="text-[12px] text-[#717680] mt-0.5">{l.industry}{l.location ? ` · ${l.location}` : ""}</div>}
                            {!l.industry && l.location && <div className="text-[12px] text-[#717680] mt-0.5">{l.location}</div>}
                          </div>
                        </div>

                        {l.company_summary && (
                          <p className="text-[13px] text-[#0A0A0A]/85 mt-2 leading-relaxed">{l.company_summary}</p>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 mt-3 text-[12px]">
                          {l.name && l.name !== l.company && (
                            <div><span className="text-[#717680]">Contact:</span> <span className="font-semibold">{l.name}</span></div>
                          )}
                          {l.email && <div className="truncate"><span className="text-[#717680]">Email:</span> <a href={`mailto:${l.email}`} className="font-mono-shelv text-[#3A86FF] hover:underline">{l.email}</a></div>}
                          {l.phone && <div><span className="text-[#717680]">Phone:</span> <span className="font-mono-shelv">{l.phone}</span></div>}
                          {l.website && (
                            <div className="truncate">
                              <span className="text-[#717680]">Website:</span>{" "}
                              <a href={l.website} target="_blank" rel="noreferrer" className="font-mono-shelv text-[#3A86FF] hover:underline">{l.website}</a>
                            </div>
                          )}
                        </div>

                        {Array.isArray(l.decision_makers) && l.decision_makers.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-1.5">
                            {l.decision_makers.slice(0, 6).map((d, k) => (
                              <span key={k} className="text-[11px] px-2 py-0.5 bg-[#F5F6F8] border border-black/10">{d}</span>
                            ))}
                          </div>
                        )}

                        {l.requirement && (
                          <div className="mt-3 text-[12px] text-[#0A0A0A]/75 italic">→ {l.requirement}</div>
                        )}

                        {dup && (
                          <div className="mt-3 text-[12px] text-[#D90429] font-semibold">
                            ⚠ {l._match_reason}
                            {l._existing_id && (
                              <button onClick={() => navigate(`/leads?focus=${l._existing_id}`)} className="ml-2 underline">view existing</button>
                            )}
                          </div>
                        )}

                        <div className="mt-3 text-[10px] font-mono-shelv text-[#717680] truncate">
                          source: {l.source_url}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {results.pages?.length > 0 && (
              <details className="border-t border-black/10 px-5 py-3">
                <summary className="text-[11px] font-mono-shelv uppercase tracking-widest text-[#717680] cursor-pointer">
                  Pages scanned ({results.pages.length})
                </summary>
                <ul className="mt-2 space-y-1 text-[11px] font-mono-shelv text-[#4A4D57]">
                  {results.pages.map((p, i) => (
                    <li key={i} className="truncate">
                      <span className="text-[#717680]">{p.leads} lead(s) · {p.chars} chars · </span>
                      <a href={p.url} target="_blank" rel="noreferrer" className="text-[#3A86FF] hover:underline">{p.url}</a>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
